import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import * as Y from 'yjs'
import { ErroreApi } from '../../servizi/api.ts'
import * as posta from '../../servizi/posta.ts'
import * as bozze from '../../servizi/bozze.ts'
import { Attesa, Avviso } from '../../componenti/Pezzi.tsx'
import { Bottone, BottoneIcona, Campo, Etichetta, Modale, Scelta, Spunta } from '../../componenti/base.tsx'
import { IconaChiudi, IconaGruppo } from '../../componenti/Icone.tsx'
import { AvvisoCifraturaWebmail, NotaAvvisoInCalce } from './AvvisoInCalce.tsx'
import { SuggerimentiIndirizzi, useSuggerimenti } from './SuggerimentiIndirizzi.tsx'
import { EditorMessaggio } from './EditorMessaggio.tsx'
import {
  RigaAllegati, RilasciaFile, useCaricamenti, useControlloAntivirus, useTrascinamento,
} from './AllegatiCompositore.tsx'
import { testoInHtml } from '@cidis/shared/html-posta'
import '../../stile-posta-messaggio.css'
/** Casella condivisa, di gruppo o di progetto (504): il messaggio parte con il nome della casella. */
const condivisa = (c: { ambito: string }) => c.ambito === 'gruppo' || c.ambito === 'progetto'


/**
 * Compositore della webmail, con la scrittura a più mani (505).
 *
 * I campi sono legati a un documento Yjs (servizi/bozze.ts). Un messaggio nuovo resta sul dispositivo
 * finché non si scrive: alla prima battuta la bozza nasce sul server, subito (3/11/2026: prima dopo un
 * attimo, e chi chiudeva la pagina in quell'attimo la perdeva), e da lì ogni modifica si salva da sola e
 * arriva a chi ha aperto la stessa bozza. Sotto i campi si legge «Bozza salvata alle 14:05». Chiudendo, la
 * bozza resta nella cartella «Bozze» della casella; «Elimina la bozza» la toglie per tutti. Lasciando la
 * pagina con qualcosa non ancora salvato il browser chiede conferma.
 *
 * Invio (3/11/2026): parte sempre dalla bozza, anche quella appena nata. Se il server di posta non lo
 * accetta il compositore resta aperto con «Non inviato» e il motivo, e il messaggio resta uno solo, nelle
 * «Bozze» della casella: «Invia» di nuovo, o «Riprova» dalla conversazione, rifanno lo stesso messaggio.
 *
 * Chi scrive dalla casella spedisce, elimina e invita colleghi su questa bozza; l'invitato scrive e
 * basta. Il contenuto spedito è quello del server: prima di «Invia» le modifiche locali partono tutte,
 * e quelle arrivate nel frattempo dagli altri sono comprese. All'invio la bozza si congela e gli
 * altri leggono «Inviata da Nome Cognome».
 *
 * Il campo «Da» elenca le caselle da cui si scrive: la propria e quelle condivise (di gruppo e di
 * progetto), tranne quelle che non spediscono (spente, o senza server di invio). Su una risposta non si sceglie: parte dalla casella che ha ricevuto la conversazione, ed è quella che il corrispondente
 * conosce. Da una casella condivisa il messaggio porta il nome della casella.
 *
 * Firma (508). Chi apre il compositore la trova già nel testo, una volta sola: in fondo al messaggio
 * nuovo, sopra la citazione nella risposta. È la sua, o quella propria della casella condivisa, e la
 * compone l'API. Cambiando «Da» si sostituisce con quella della nuova casella; «Senza il mio nome»,
 * sulle caselle condivise, toglie il nome di chi scrive. Il compositore tocca solo una firma che
 * riconosce come sua (posta.firmeNote): una firma modificata a mano, o quella di chi ha creato la
 * bozza condivisa, resta com'è. Nella bozza condivisa la firma entra con il documento di chi la crea:
 * chi la apre dopo la trova e non ne aggiunge un'altra.
 */

/** Campi iniziali di un messaggio nuovo, di una risposta o di un inoltro. */
export type Bozza = {
  /** La casella da cui parte: su una risposta è quella che ha ricevuto la conversazione. */
  casella_id: string
  a: string; cc: string; ccn: string; oggetto: string; testo: string; in_risposta_a?: string
  /** Allegati già pronti: «Inoltra come allegato» (539) mette il messaggio come file .eml. */
  allegati?: posta.AllegatoDaInviare[]
  /** Una nota per chi scrive, sopra i campi: per esempio gli allegati rimasti fuori dal .eml. */
  nota?: string
}

/** Che cosa apre il compositore: un messaggio da scrivere, oppure una bozza che c'è già. */
export type AperturaCompositore = { nuova: Bozza } | { bozza: string }

const testoErrore = (e: unknown) => (e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')

/** «14:05»: l'ora dell'ultimo salvataggio. */
const oraBreve = (d: Date) => d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })

/** Come indirizziDa nell'API: di «Nome <a@b.it>» resta l'indirizzo, che e' quello che l'invio accetta. */
const indirizzi = (testo: string) => testo.split(/[,;]/).map(posta.soloIndirizzo).filter(Boolean)

/**
 * Legame fra un campo e un testo condiviso.
 *
 * Il campo non è governato da React: le modifiche altrui si scrivono nel campo nello stesso istante
 * in cui entrano nel documento, e il cursore di chi scrive si sposta di quanto il testo prima di lui
 * è cresciuto o calato. Così il campo e il documento non possono mai divergere, nemmeno se una battuta
 * arriva mentre React non ha ancora ridisegnato. La battuta locale diventa la modifica minima
 * (bozze.differenza) sul testo che il campo mostrava.
 */
function useTestoCondiviso<T extends HTMLInputElement | HTMLTextAreaElement>(testo: Y.Text) {
  const riferimento = useRef<T>(null)
  const mostrato = useRef(testo.toString())

  useLayoutEffect(() => {
    const campo = riferimento.current
    mostrato.current = testo.toString()
    if (campo && campo.value !== mostrato.current) campo.value = mostrato.current
    const osserva = (evento: Y.YTextEvent, transazione: Y.Transaction) => {
      if (transazione.origin === bozze.ORIGINE_LOCALE) return
      const nuovo = testo.toString()
      const el = riferimento.current
      if (el && el.value !== nuovo) {
        const attivo = document.activeElement === el
        const inizio = el.selectionStart ?? 0
        const fine = el.selectionEnd ?? 0
        const scorrimento = el.scrollTop
        el.value = nuovo
        if (attivo) el.setSelectionRange(bozze.trasforma(inizio, evento.delta), bozze.trasforma(fine, evento.delta))
        el.scrollTop = scorrimento
      }
      mostrato.current = nuovo
    }
    testo.observe(osserva)
    return () => testo.unobserve(osserva)
  }, [testo])

  const cambia = useCallback((nuovo: string) => {
    const d = bozze.differenza(mostrato.current, nuovo, riferimento.current?.selectionEnd ?? nuovo.length)
    mostrato.current = nuovo
    if (!d) return
    testo.doc!.transact(() => {
      if (d.togli) testo.delete(d.da, d.togli)
      if (d.metti) testo.insert(d.da, d.metti)
    }, bozze.ORIGINE_LOCALE)
  }, [testo])

  return { riferimento, cambia, iniziale: mostrato.current }
}

/** Ridisegna a ogni modifica del documento: serve ai controlli che dipendono dal contenuto. */
function useDocumento(doc: Y.Doc): void {
  const [, setVersione] = useState(0)
  useEffect(() => {
    const aggiorna = () => setVersione((v) => v + 1)
    doc.on('update', aggiorna)
    return () => doc.off('update', aggiorna)
  }, [doc])
}

/** Ridisegna quando la sessione cambia stato, presenze o salvataggio. */
function useSessioneBozza(sessione: bozze.SessioneBozza | null): void {
  const [, setVersione] = useState(0)
  useEffect(() => sessione?.ascolta(() => setVersione((v) => v + 1)), [sessione])
}

export function Compositore({ caselle: tutte, apertura, firme, chiudi, spedito, nonInviato }: {
  /** Le caselle da cui si scrive: il campo «Da» propone quelle che spediscono. */
  caselle: posta.CasellaWebmail[]
  apertura: AperturaCompositore
  /** Le firme di chi scrive, per casella (508); null se non si sono potute leggere. */
  firme: posta.FirmeDellaPersona | null
  chiudi: () => void
  /** Dopo l'invio, con la conversazione del messaggio: la scheda da cui si scrive la aggancia (2/11/2026). */
  spedito: (esito?: { thread_id?: string; stato_invio?: string }) => void
  /**
   * Invio non riuscito (3/11/2026): il compositore resta aperto con il motivo. Chi lo apre ne riceve la
   * conversazione, che c'è già: la scheda la aggancia lo stesso, come prima.
   */
  nonInviato?: (esito: { id: string; thread_id?: string; errore: string | null }) => void
}) {
  // Casella spenta o senza server di invio (3/11/2026): non è un mittente.
  const caselle = useMemo(() => tutte.filter((c) => c.spedisce !== false), [tutte])
  const nuova = 'nuova' in apertura ? apertura.nuova : null
  const [doc] = useState(() => {
    const d = bozze.nuovoDocumento()
    if (nuova) {
      for (const campo of bozze.CAMPI_TESTO) if (nuova[campo]) d.getText(campo).insert(0, nuova[campo])
      if (nuova.allegati?.length) bozze.allegatiDi(d).push(nuova.allegati)
    }
    return d
  })
  const [info, setInfo] = useState<bozze.StatoIniziale | null>(null)
  const [sessione, setSessione] = useState<bozze.SessioneBozza | null>(null)
  const [carico, setCarico] = useState(!nuova)
  const [casellaId, setCasellaId] = useState(nuova?.casella_id ?? '')
  const [inviti, setInviti] = useState<bozze.Invito[]>([])
  const [invitando, setInvitando] = useState(false)
  const [copie, setCopie] = useState(!!nuova?.cc)
  const [inCorso, setInCorso] = useState(false)
  const [confermaElimina, setConfermaElimina] = useState(false)
  const [senzaNome, setSenzaNome] = useState(false)
  const [limiti, setLimiti] = useState<posta.LimitiAllegati>(posta.LIMITI_PREDEFINITI)
  // Scadenza (510): la sceglie chi spedisce, al momento dell'invio; non entra nella bozza condivisa.
  const [modoScadenza, setModoScadenza] = useState<'' | posta.ModoScadenza>('')
  const [scadeIl, setScadeIl] = useState(() => posta.fraGiorni(7))
  const [passwordScadenza, setPasswordScadenza] = useState('')
  const [mostraOggetto, setMostraOggetto] = useState(false)
  // Conferma di lettura e priorità (539): come la scadenza, le sceglie chi spedisce e non entrano nella bozza.
  const [confermaLettura, setConfermaLettura] = useState(false)
  const [prioritaAlta, setPrioritaAlta] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  // L'ultimo invio non riuscito: il motivo, detto sopra i campi finché non si spedisce.
  const [nonPartito, setNonPartito] = useState<{ errore: string | null } | null>(null)
  const sessioneRef = useRef<bozze.SessioneBozza | null>(null)
  const creazione = useRef<Promise<bozze.SessioneBozza | null> | null>(null)
  const modificata = useRef(false)

  useDocumento(doc)
  useSessioneBozza(sessione)

  const inRispostaA = nuova?.in_risposta_a ?? info?.bozza.in_risposta_a ?? null
  const risposta = !!inRispostaA
  // Chi entra con un invito non scrive dalla casella: scrive nella bozza e basta.
  const scrive = info ? info.scrive : true
  const casella = caselle.find((c) => c.id === casellaId) ?? (risposta || info ? undefined : caselle[0])
  const indirizzoCasella = info?.bozza.casella.indirizzo ?? casella?.indirizzo ?? ''
  // La bozza sta in una casella che non spedisce (spenta dopo che la si è scritta): si sceglie un'altra casella.
  const casellaFerma = !!info && info.scrive && !casella
  const testoFerma = tutte.find((c) => c.id === casellaId)?.non_spedisce
    ?? `La casella ${indirizzoCasella} è spenta, o non ha un server di invio: non spedisce.`
  const stato = sessione?.stato ?? info?.bozza.stato ?? 'aperta'
  const inviataDa = sessione?.inviataDa ?? info?.bozza.inviata_da ?? null
  const persa = sessione?.persa ?? false
  const bloccata = stato !== 'aperta' || persa || carico

  /** Avvia la sessione sulla bozza appena creata o aperta. */
  const avvia = useCallback((s: bozze.StatoIniziale) => {
    const nuovaSessione = new bozze.SessioneBozza(doc, {
      id: s.bozza.id, seq: s.seq, stato: s.bozza.stato, inviataDa: s.bozza.inviata_da,
      presenti: s.presenti, presenza: s.presenza,
    })
    // «Bozza salvata alle …»: dall'ultima modifica che il server conosce.
    nuovaSessione.salvataIl = new Date(s.bozza.modificata_il)
    sessioneRef.current = nuovaSessione
    setInfo(s)
    if (s.non_inviato) setNonPartito({ errore: s.non_inviato.errore })
    setInviti(s.inviti ?? [])
    setCasellaId(s.bozza.casella.id)
    setSessione(nuovaSessione)
    nuovaSessione.avvia()
    return nuovaSessione
  }, [doc])

  /**
   * La bozza nasce sul server con lo stato del documento. Quello che si scrive mentre la richiesta
   * viaggia parte subito dopo, come differenza dallo stato mandato.
   */
  const creaOra = useCallback((): Promise<bozze.SessioneBozza | null> => {
    if (sessioneRef.current) return Promise.resolve(sessioneRef.current)
    if (creazione.current) return creazione.current
    const vettore = Y.encodeStateVector(doc)
    creazione.current = bozze.creaBozza({
      casella_id: casellaId,
      ...(inRispostaA ? { in_risposta_a: inRispostaA } : {}),
      stato: bozze.inBase64(Y.encodeStateAsUpdate(doc)),
    }).then((s) => {
      const nuovaSessione = avvia(s)
      nuovaSessione.accoda(Y.encodeStateAsUpdate(doc, vettore))
      return nuovaSessione
    }).catch((e: unknown) => {
      creazione.current = null
      setErrore(`La bozza non si salva: ${testoErrore(e)}`)
      return null
    })
    return creazione.current
  }, [doc, casellaId, inRispostaA, avvia])

  // Messaggio nuovo: alla prima battuta di chi scrive la bozza nasce sul server, subito. Quello che si scrive
  // mentre la richiesta viaggia parte dopo, come differenza (creaOra).
  useEffect(() => {
    if (!nuova) return
    const suModifica = (_: Uint8Array, origine: unknown) => {
      if (origine !== bozze.ORIGINE_LOCALE || sessioneRef.current || creazione.current) return
      modificata.current = true
      void creaOra()
    }
    doc.on('update', suModifica)
    return () => doc.off('update', suModifica)
  }, [doc, nuova, creaOra])

  // Lasciando la pagina con qualcosa non ancora salvato, il browser chiede conferma. Le modifiche partono
  // comunque all'uscita (servizi/bozze.ts): la conferma copre la bozza che sta nascendo, o il server che non
  // risponde.
  useEffect(() => {
    const allUscita = (e: BeforeUnloadEvent) => {
      const s = sessioneRef.current
      const nonSalvato = s ? s.daSalvare || !!s.problemaSalvataggio : creazione.current !== null || modificata.current
      if (!nonSalvato) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', allUscita)
    return () => window.removeEventListener('beforeunload', allUscita)
  }, [])

  // Bozza che c'è già: si apre dal server.
  useEffect(() => {
    if (!('bozza' in apertura)) return
    let vivo = true
    bozze.apriBozza(apertura.bozza).then((s) => {
      if (!vivo) return
      if (s.stato_yjs) Y.applyUpdate(doc, bozze.daBase64(s.stato_yjs), bozze.ORIGINE_REMOTA)
      avvia(s)
    }).catch((e) => { if (vivo) setErrore(testoErrore(e)) })
      .finally(() => { if (vivo) setCarico(false) })
    return () => { vivo = false }
    // La bozza si apre una volta sola, alla comparsa del compositore.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Soglia e massimo degli allegati (509): servono a dire prima dell'invio che cosa parte come collegamento.
  useEffect(() => { posta.leggiLimitiAllegati().then(setLimiti).catch(() => undefined) }, [])

  // Il compositore che sparisce chiude la sessione: le ultime modifiche partono, la presenza sparisce.
  useEffect(() => () => {
    sessioneRef.current?.chiudi()
  }, [])

  const legami = {
    a: useTestoCondiviso<HTMLInputElement>(doc.getText('a')),
    cc: useTestoCondiviso<HTMLInputElement>(doc.getText('cc')),
    ccn: useTestoCondiviso<HTMLInputElement>(doc.getText('ccn')),
    oggetto: useTestoCondiviso<HTMLInputElement>(doc.getText('oggetto')),
    testo: useTestoCondiviso<HTMLTextAreaElement>(doc.getText('testo')),
  }
  const fuoco = (campo: bozze.Campo) => () => sessioneRef.current?.campo(campo)
  // Rubrica della casella da cui si scrive e colleghi (527): i suggerimenti di A, Cc e Ccn.
  const suggerimenti = useSuggerimenti(casella?.id)

  // Una copia scritta da un altro apre i campi della copia, e restano aperti.
  const conCopie = copie || doc.getText('cc').length > 0 || doc.getText('ccn').length > 0
  useEffect(() => { if (conCopie && !copie) setCopie(true) }, [conCopie, copie])
  const allegati = bozze.allegatiDi(doc).toArray()
  // Gli allegati che partiranno come collegamento (509): lo decide l'API all'invio, con la stessa regola.
  const collegati = posta.allegatiDaCollegare(allegati, limiti.soglia_byte)
  // Allegati in caricamento, non caricati, rifiutati, e i file trascinati nella finestra (3/11/2026).
  const caricamenti = useCaricamenti({
    doc, limiti, quanti: allegati.length, allCampo: () => sessioneRef.current?.campo('allegati'),
  })
  const trascinando = useTrascinamento(!bloccata, (files) => void caricamenti.allega(files))
  const [chiediSenza, setChiediSenza] = useState(false)
  // Antivirus prima dell'invio (566): ogni allegato «In controllo…», «Pronto» o tolto se infetto; «Invia» aspetta.
  const controllo = useControlloAntivirus(allegati, togliAllegato, caricamenti.avvisa, !bloccata)
  const inControllo = controllo.nonPronti.filter((a) => controllo.esitoDi(a.storage_key) === 'in_attesa')

  /** La firma che il compositore riconosce nel testo, o null: la più lunga fra quelle note. */
  function firmaNelTesto(): { inizio: number; firma: string } | null {
    const attuale = doc.getText('testo').toString()
    for (const firma of posta.firmeNote(firme)) {
      const inizio = attuale.indexOf(firma)
      if (inizio >= 0) return { inizio, firma }
    }
    return null
  }

  /**
   * Sostituisce la firma riconosciuta con un'altra; senza firma riconosciuta il testo non cambia. Nel
   * corpo con la formattazione (EditorMessaggio) la firma e' il suo testo con gli a capo come <br>: si
   * sostituisce anche li', se c'e' ancora com'era.
   */
  function sostituisciFirma(nuova: string) {
    const trovata = firmaNelTesto()
    if (!trovata || !nuova || trovata.firma === nuova) return
    const testo = doc.getText('testo')
    const html = doc.getText('html')
    const vecchiaHtml = testoInHtml(trovata.firma)
    const inizioHtml = html.toString().indexOf(vecchiaHtml)
    doc.transact(() => {
      testo.delete(trovata.inizio, trovata.firma.length)
      testo.insert(trovata.inizio, nuova)
      if (inizioHtml >= 0) {
        html.delete(inizioHtml, vecchiaHtml.length)
        html.insert(inizioHtml, testoInHtml(nuova))
      }
    }, bozze.ORIGINE_PROGRAMMA)
  }

  function cambiaSenzaNome(valore: boolean) {
    setSenzaNome(valore)
    if (casella) sostituisciFirma(posta.firmaPer(firme, casella.id, valore))
  }

  function cambiaMittente(id: string) {
    if (!id) return
    sostituisciFirma(posta.firmaPer(firme, id, senzaNome))
    setCasellaId(id)
    const s = sessioneRef.current
    if (s) bozze.spostaBozza(s.id, id).catch((e) => setErrore(testoErrore(e)))
  }

  function togliAllegato(chiave: string) {
    const elenco = bozze.allegatiDi(doc)
    const posizione = elenco.toArray().findIndex((a) => a.storage_key === chiave)
    if (posizione >= 0) doc.transact(() => elenco.delete(posizione, 1), bozze.ORIGINE_LOCALE)
  }

  /** La scadenza da mandare con l'invio, o null; un errore se la data o la password non vanno. */
  function scadenzaScelta(): posta.ScadenzaDaInviare | null {
    if (!modoScadenza) return null
    const quando = new Date(scadeIl)
    if (Number.isNaN(quando.getTime())) throw new Error('Scrivi la data e l’ora della scadenza.')
    if (modoScadenza === 'allegati' && !allegati.length) {
      throw new Error('Senza allegati non c’è niente che scada: scegli «Scade il messaggio» o nessuna scadenza.')
    }
    if (modoScadenza === 'messaggio' && passwordScadenza && passwordScadenza.length < 6) {
      throw new Error('La password del messaggio ha almeno sei caratteri, oppure lasciala vuota.')
    }
    return {
      modo: modoScadenza, scade_il: quando.toISOString(),
      ...(modoScadenza === 'messaggio' ? { password: passwordScadenza || null, mostra_oggetto: mostraOggetto } : {}),
    }
  }

  /**
   * Nomi di gruppi scritti per intero in A, Cc e Ccn (539): diventano i loro indirizzi prima dell'invio, nel
   * campo e quindi anche nella bozza condivisa. I suggerimenti lo fanno gia' all'uscita dal campo.
   */
  function espandiGruppiNeiCampi() {
    for (const campo of ['a', 'cc', 'ccn'] as const) {
      const nuovo = posta.espandiGruppi(doc.getText(campo).toString(), suggerimenti)
      if (nuovo === null) continue
      const el = legami[campo].riferimento.current
      if (el) el.value = nuovo
      legami[campo].cambia(nuovo)
    }
  }

  /**
   * Invio, sempre dalla bozza (3/11/2026): quella che c'è, o quella che nasce ora. Prima si aspetta che le
   * modifiche locali siano tutte sul server. Se il server di posta non accetta il messaggio il compositore
   * resta aperto con il motivo: il messaggio è uno solo, nelle «Bozze», e «Invia» lo rifà.
   */
  async function invia(senzaNonCaricati = false) {
    // Un file che non e' arrivato non parte: lo si dice prima, e si spedisce senza solo se lo si conferma.
    if (caricamenti.nonCaricati.length && !senzaNonCaricati) { setChiediSenza(true); return }
    setChiediSenza(false)
    setInCorso(true); setErrore(null)
    try {
      espandiGruppiNeiCampi()
      const scadenza = scadenzaScelta()
      if (!casella && !info) return
      const s = sessioneRef.current ?? await creaOra()
      if (!s) return
      if (!(await s.attendiSalvataggio())) {
        setErrore('Le ultime modifiche non sono ancora salvate: riprova fra poco.')
        return
      }
      const esito = await bozze.inviaBozza(s.id, scadenza, { conferma_lettura: confermaLettura, priorita_alta: prioritaAlta })
      if (esito.stato_invio !== 'inviato') {
        setNonPartito({ errore: esito.errore })
        nonInviato?.({ id: esito.id, thread_id: esito.thread_id, errore: esito.errore })
        return
      }
      setNonPartito(null)
      s.chiudi()
      spedito({ thread_id: esito.thread_id, stato_invio: esito.stato_invio })
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : (e as Error).message || testoErrore(e))
    } finally { setInCorso(false) }
  }

  /** Chiudi: la bozza resta. Se si è appena scritto qualcosa, nasce ora invece che fra un attimo. */
  async function esci() {
    setInCorso(true)
    try {
      if (!sessioneRef.current && modificata.current) await creaOra()
      const s = sessioneRef.current
      if (s) await s.attendiSalvataggio()
    } finally {
      setInCorso(false)
      chiudi()
    }
  }

  async function elimina() {
    if (!sessioneRef.current && !creazione.current) { chiudi(); return }
    setInCorso(true); setErrore(null)
    try {
      const s = sessioneRef.current ?? await creazione.current
      if (s) await bozze.eliminaBozza(s.id)
      chiudi()
    } catch (e) {
      setErrore(testoErrore(e))
      setConfermaElimina(false)
    } finally { setInCorso(false) }
  }

  async function apriInviti() {
    const s = await creaOra()
    if (s) setInvitando(true)
  }

  async function revoca(utenteId: string) {
    const s = sessioneRef.current
    if (!s) return
    try {
      setInviti(await bozze.revocaInvito(s.id, utenteId))
    } catch (e) { setErrore(testoErrore(e)) }
  }

  const pronto = !!casella && indirizzi(doc.getText('a').toString()).length > 0
    && doc.getText('oggetto').toString().trim().length > 0
  const etichettaCasella = (c: posta.CasellaWebmail) => `${c.nome_visualizzato || c.indirizzo} <${c.indirizzo}>`
  const titolo = info && !scrive
    ? `Bozza condivisa · ${indirizzoCasella}`
    : casella && (risposta || caselle.length < 2) ? `Scrivi da ${casella.indirizzo}` : 'Nuovo messaggio'
  const presenti = sessione?.presenti ?? []
  const problema = sessione?.problemaSalvataggio ?? sessione?.problemaRicezione ?? null

  if (carico) {
    return (
      <Modale titolo="Bozza" chiudi={chiudi}>
        <Attesa testo="Apertura della bozza…" />
        {errore && <Avviso tipo="errore">{errore}</Avviso>}
      </Modale>
    )
  }

  return (
    // Finestra larga (2/11/2026, «la webmail è troppo piccola»): il messaggio si scrive su tutta la larghezza.
    <Modale
      larga
      titolo={titolo}
      chiudi={() => void esci()}
      azioni={
        <>
          {/* Una bozza salvata si elimina per tutti, con una conferma; un messaggio mai salvato si scarta. */}
          {scrive && !persa && stato !== 'inviata' && (
            <Bottone variante="critico" disabled={inCorso || stato === 'in_invio'}
                     onClick={() => (!sessione || confermaElimina ? void elimina() : setConfermaElimina(true))}>
              {!sessione ? 'Scarta' : confermaElimina ? 'Conferma: elimina per tutti' : 'Elimina la bozza'}
            </Bottone>
          )}
          <Bottone variante="secondario" disabled={inCorso} onClick={() => void esci()}>Chiudi</Bottone>
          {/* «Invia» aspetta i file in caricamento, e dice quali. */}
          {scrive && caricamenti.inCorso.length > 0 && (
            <span className="piccolo tenue posta-invio-attesa" role="status">
              Caricamento di {caricamenti.inCorso.map((n) => `«${n}»`).join(', ')} in corso…
            </span>
          )}
          {/* «Invia» aspetta anche l'antivirus (566), e dice quali file. */}
          {scrive && caricamenti.inCorso.length === 0 && controllo.nonPronti.length > 0 && (
            <span className="piccolo tenue posta-invio-attesa" role="status">
              {inControllo.length
                ? `Invia si attiva quando l’antivirus ha controllato ${inControllo.length === 1
                  ? `«${inControllo[0]!.nome}»` : `i ${inControllo.length} allegati`}.`
                : `Togli ${controllo.troppoGrandi.map((a) => `«${a.nome}»`).join(', ')}: l’antivirus non lo può `
                  + 'controllare, e non parte.'}
            </span>
          )}
          {scrive && (
            <Bottone variante="primario"
                     disabled={inCorso || !pronto || bloccata || caricamenti.inCorso.length > 0 || controllo.nonPronti.length > 0}
                     onClick={() => void invia()}>Invia</Bottone>
          )}
        </>
      }
    >
      {/* Antivirus fermo (566, 565): gli allegati restano in controllo e il messaggio non parte; lo si dice in cima. */}
      {controllo.fermo && inControllo.length > 0 && stato === 'aperta' && (
        <Avviso tipo="attenzione">
          <strong>L’antivirus non risponde</strong>: {inControllo.length === 1 ? 'l’allegato resta' : 'gli allegati restano'}
          {' '}in controllo e il messaggio non può partire finché non {inControllo.length === 1 ? 'è controllato' : 'sono controllati'}.
          Chiudi pure: il messaggio resta nelle «Bozze» e lo invii più tardi. Oppure togli gli allegati e invia
          senza. Amministratori e Direzione sono già avvisati.
        </Avviso>
      )}
      {controllo.errore && <Avviso tipo="attenzione">{controllo.errore}</Avviso>}
      {/* Non partito (3/11/2026): il compositore resta aperto, e il messaggio resta uno solo nelle «Bozze». */}
      {nonPartito && stato === 'aperta' && !persa && (
        <Avviso tipo="errore">
          <strong>Non inviato</strong>: {posta.motivoNonInviato(nonPartito.errore)}. Il messaggio resta uno solo, nelle
          «Bozze» di {indirizzoCasella}, con la scritta «Non inviato»: correggi se serve e premi di nuovo «Invia», o
          riprova più tardi dalla conversazione.
        </Avviso>
      )}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {casellaFerma && (
        <Avviso tipo="attenzione">
          {testoFerma}
          {risposta
            ? ' Una risposta parte dalla casella che ha ricevuto la conversazione: copia il testo in un messaggio nuovo da un’altra casella.'
            : ' Scegli la casella in «Da».'}
        </Avviso>
      )}
      {persa && (
        <Avviso tipo="errore">
          Questa bozza non c’è più, oppure non puoi più aprirla: è stata eliminata o l’invito è stato revocato.
        </Avviso>
      )}
      {stato === 'inviata' && (
        <Avviso tipo="successo">
          Inviata{inviataDa ? ` da ${inviataDa.nome} ${inviataDa.cognome}` : ''}. Non si modifica più.
        </Avviso>
      )}
      {stato === 'in_invio' && (
        <Avviso tipo="informazione">
          Invio in corso{inviataDa ? ` da ${inviataDa.nome} ${inviataDa.cognome}` : ''}: per ora non si modifica.
        </Avviso>
      )}
      {!info && !nuova && <Avviso tipo="errore">Non hai più accesso alla casella di questa conversazione.</Avviso>}
      {nuova && !casella && <Avviso tipo="errore">Non hai più accesso alla casella di questa conversazione.</Avviso>}

      {info && !scrive && (
        <Avviso tipo="informazione">
          {info.invitata_da ? `${info.invitata_da.nome} ${info.invitata_da.cognome} ti ha invitato` : 'Sei invitato'} a
          scrivere questa bozza della casella {indirizzoCasella}. Vedi solo la bozza e il messaggio a cui risponde;
          la spedisce chi scrive dalla casella.
        </Avviso>
      )}

      {/* Chi c'e' adesso, sopra il riquadro, e lo stato del salvataggio: finche' la bozza si scrive. */}
      {!bloccata && presenti.length > 0 && (
        <p className="piccolo"><IconaGruppo /> Sta scrivendo anche: {bozze.descriviPresenti(presenti)}</p>
      )}
      {!bloccata && (
        <p className="piccolo tenue posta-salvataggio" role="status">
          {problema
            ?? (sessione
              ? sessione.inSospeso || !sessione.salvataIl ? 'Salvataggio…' : `Bozza salvata alle ${oraBreve(sessione.salvataIl)}`
              : creazione.current ? 'Salvataggio…' : 'La bozza si salva da sola appena scrivi.')}
        </p>
      )}

      {/* Su una risposta la casella e' data dalla conversazione e si legge nel titolo. */}
      {scrive && !risposta && (caselle.length > 1 || (casellaFerma && caselle.length > 0)) && (
        <Scelta etichetta="Da" obbligatorio valore={casella ? casellaId : ''} cambia={cambiaMittente} disabilitato={bloccata}
                {...(casella ? {} : { vuota: 'Scegli la casella da cui spedire' })}
                opzioni={caselle.map((c) => ({
                  valore: c.id,
                  etichetta: condivisa(c) ? `${etichettaCasella(c)} · ${c.ambito}` : etichettaCasella(c),
                }))} />
      )}
      {casella && condivisa(casella) && (
        <p className="piccolo tenue">
          Il messaggio parte da «{casella.nome_visualizzato}» &lt;{casella.indirizzo}&gt;.
        </p>
      )}
      {/* «Senza il mio nome» (508): sulle caselle condivise, finché la firma è quella del compositore. */}
      {scrive && casella && condivisa(casella) && !bloccata && (
        <Spunta etichetta="Senza il mio nome" valore={senzaNome} cambia={cambiaSenzaNome}
                disabilitato={!firmaNelTesto()}
                aiuto={firmaNelTesto()
                  ? 'Toglie il tuo nome dalla firma: il messaggio resta firmato dalla casella.'
                  : 'La firma nel testo è stata modificata, o l’ha inserita chi ha creato la bozza: '
                    + 'correggila a mano.'} />
      )}

      {scrive && stato !== 'inviata' && !persa && (
        <div className="riga">
          <Bottone variante="secondario" dimensione="piccolo" icona={<IconaGruppo />} disabled={inCorso || !casella}
                   onClick={() => void apriInviti()}>
            Invita a scrivere
          </Bottone>
          {inviti.map((i) => (
            <Etichetta key={i.utente.id}>
              {i.utente.nome} {i.utente.cognome}
              <BottoneIcona icona={<IconaChiudi />} etichetta={`Revoca l’invito a ${i.utente.nome} ${i.utente.cognome}`}
                            onClick={() => void revoca(i.utente.id)} />
            </Etichetta>
          ))}
        </div>
      )}

      {nuova?.nota && <Avviso tipo="informazione">{nuova.nota}</Avviso>}
      {info?.risposta_a && <MessaggioCitato messaggio={info.risposta_a} />}

      <SuggerimentiIndirizzi campo={legami.a.riferimento} cambia={legami.a.cambia} voci={suggerimenti}>
        <Campo etichetta="A" obbligatorio cambia={legami.a.cambia} iniziale={legami.a.iniziale}
               riferimento={legami.a.riferimento} suFuoco={fuoco('a')} disabilitato={bloccata}
               segnaposto="indirizzi separati da virgola" />
      </SuggerimentiIndirizzi>

      {conCopie ? (
        <>
          <SuggerimentiIndirizzi campo={legami.cc.riferimento} cambia={legami.cc.cambia} voci={suggerimenti}>
            <Campo etichetta="Copia (cc)" cambia={legami.cc.cambia} iniziale={legami.cc.iniziale}
                   riferimento={legami.cc.riferimento} suFuoco={fuoco('cc')} disabilitato={bloccata} />
          </SuggerimentiIndirizzi>
          <SuggerimentiIndirizzi campo={legami.ccn.riferimento} cambia={legami.ccn.cambia} voci={suggerimenti}>
            <Campo etichetta="Copia nascosta (ccn)" cambia={legami.ccn.cambia} iniziale={legami.ccn.iniziale}
                   riferimento={legami.ccn.riferimento} suFuoco={fuoco('ccn')} disabilitato={bloccata}
                   aiuto="Gli indirizzi in copia nascosta non compaiono agli altri destinatari." />
          </SuggerimentiIndirizzi>
        </>
      ) : (
        <div className="riga">
          <Bottone variante="discreto" dimensione="piccolo" onClick={() => setCopie(true)}>
            Aggiungi copia e copia nascosta
          </Bottone>
        </div>
      )}

      {casella?.cifratura_assente?.includes('invio') && <AvvisoCifraturaWebmail casella={casella} />}

      <Campo etichetta="Oggetto" obbligatorio cambia={legami.oggetto.cambia} iniziale={legami.oggetto.iniziale}
             riferimento={legami.oggetto.riferimento} suFuoco={fuoco('oggetto')} disabilitato={bloccata} />
      <EditorMessaggio doc={doc} disabilitato={bloccata} suFuoco={fuoco('testo')} />
      <NotaAvvisoInCalce />

      <RigaAllegati allegati={allegati} collegati={collegati} togliAllegato={togliAllegato} bloccata={bloccata}
                    gestione={caricamenti} controllo={controllo} />
      {trascinando && <RilasciaFile />}
      {chiediSenza && (
        <Modale titolo="Allegati non caricati" chiudi={() => setChiediSenza(false)} fuocoIniziale="azioni" azioni={(
          <>
            <Bottone onClick={() => setChiediSenza(false)}>Annulla</Bottone>
            <Bottone variante="primario" onClick={() => void invia(true)}>Spedisci senza</Bottone>
          </>
        )}>
          <p>
            {caricamenti.nonCaricati.length === 1
              ? <>L’allegato «{caricamenti.nonCaricati[0]}» non è stato caricato. Spedire senza?</>
              : <>Gli allegati {caricamenti.nonCaricati.map((n) => `«${n}»`).join(', ')} non sono stati caricati. Spedire senza?</>}
          </p>
          <p className="piccolo tenue">Con «Annulla» torni al messaggio: puoi riprovare a caricarli o toglierli.</p>
        </Modale>
      )}

      {/* Conferma di lettura e priorità (539): le decide chi spedisce, al momento dell'invio. */}
      {scrive && !bloccata && (
        <div className="posta-opzioni-invio">
          <Spunta etichetta="Chiedi la conferma di lettura" valore={confermaLettura} cambia={setConfermaLettura}
                  aiuto="Chi riceve sceglie se mandarla: molti programmi di posta la chiedono, altri non la mandano mai." />
          <Spunta etichetta="Priorità alta" valore={prioritaAlta} cambia={setPrioritaAlta}
                  aiuto="Il messaggio arriva con la priorità alta: molti programmi lo segnano con un «!»." />
        </div>
      )}

      {/* Scadenza (510): la decide chi spedisce, al momento dell'invio. */}
      {scrive && !bloccata && (
        <details className="posta-scadenza" open={!!modoScadenza}>
          <summary>Scadenza{modoScadenza ? `: ${modoScadenza === 'messaggio' ? 'scade il messaggio' : 'scadono gli allegati'}` : ''}</summary>
          <Scelta etichetta="Scadenza" valore={modoScadenza} cambia={(v) => setModoScadenza(v as '' | posta.ModoScadenza)}
                  opzioni={[
                    { valore: '', etichetta: 'Nessuna' },
                    { valore: 'messaggio', etichetta: 'Scade il messaggio' },
                    { valore: 'allegati', etichetta: 'Scadono solo gli allegati' },
                  ]}
                  aiuto={modoScadenza === 'messaggio'
                    ? 'Ai destinatari fuori da CIDIS arriva solo un avviso con il collegamento a una pagina, che mostra il messaggio fino alla scadenza. Ai colleghi arriva normale.'
                    : modoScadenza === 'allegati'
                      ? 'Il testo arriva normale; gli allegati, tutti, partono come collegamento e non si scaricano più dopo la scadenza.'
                      : undefined} />
          {modoScadenza && (
            <>
              <div className="riga">
                <Campo etichetta="Scade il" tipo="datetime-local" valore={scadeIl} cambia={setScadeIl} />
                {[1, 7, 30].map((g) => (
                  <Bottone key={g} variante="discreto" dimensione="piccolo" onClick={() => setScadeIl(posta.fraGiorni(g))}>
                    {g === 1 ? '1 giorno' : `${g} giorni`}
                  </Bottone>
                ))}
              </div>
              {modoScadenza === 'messaggio' && (
                <>
                  <Campo etichetta="Password (facoltativa)" tipo="password" valore={passwordScadenza}
                         cambia={setPasswordScadenza} autoComplete="new-password"
                         aiuto="Almeno sei caratteri. Comunicala a voce, non per email: la pagina la chiede prima di mostrare il messaggio." />
                  <Spunta etichetta="Mostra l’oggetto nell’avviso" valore={mostraOggetto} cambia={setMostraOggetto}
                          aiuto="Senza, l’avviso dice «Messaggio riservato da …»: l’oggetto può già dire troppo." />
                </>
              )}
            </>
          )}
        </details>
      )}

      {collegati.size > 0 && !modoScadenza && (
        <Avviso tipo="informazione">
          {collegati.size === 1 ? 'Questo file partirà come collegamento' : 'Questi file partiranno come collegamento'}:{' '}
          {allegati.filter((_, i) => collegati.has(i)).map((a) => `«${a.nome}»`).join(', ')}. Insieme pesano troppo
          per i server di posta: nel messaggio, subito prima della firma, il gestionale mette la tabella
          «Allegati» con il collegamento per scaricarli. Chi ha il collegamento scarica il file, finché non lo
          revochi: per dati particolari (salute, documenti d’identità) meglio non usarlo.
        </Avviso>
      )}

      {invitando && sessione && (
        <InvitaAScrivere bozzaId={sessione.id} risposta={risposta}
                         chiudi={() => setInvitando(false)} invitati={setInviti} />
      )}
    </Modale>
  )
}

/**
 * Il messaggio a cui la bozza risponde, chiuso finché non lo si apre. Per l'invitato è l'unico
 * messaggio della casella che vede. L'HTML si mostra come nella lettura: ripulito, in un riquadro
 * isolato e senza immagini remote.
 */
function MessaggioCitato({ messaggio }: { messaggio: posta.Messaggio }) {
  const [aperto, setAperto] = useState(false)
  const corpo = useMemo(
    () => (messaggio.html ? posta.ripulisciHtml(messaggio.html, false) : null), [messaggio.html])
  return (
    <div className="posta-messaggio">
      <header>
        <button type="button" className="posta-alterna" onClick={() => setAperto((a) => !a)} aria-expanded={aperto}>
          <span className="testo">
            <span className="chi">In risposta a {posta.nomeBreve(messaggio.da)}</span>
            <span className="dettaglio">{messaggio.oggetto}</span>
          </span>
          <span className="quando">{posta.dataEstesa(messaggio.data)}</span>
        </button>
      </header>
      {aperto && (
        <div className="posta-contenuto">
          {corpo ? (
            <iframe className="posta-html" sandbox="" title={`Messaggio del ${posta.dataEstesa(messaggio.data)}`}
                    srcDoc={posta.paginaMessaggio(corpo.html, false)} />
          ) : (
            <p className="posta-testo">{messaggio.testo || '(messaggio senza testo)'}</p>
          )}
          {messaggio.allegati.length > 0 && (
            <p className="piccolo tenue">Allegati: {messaggio.allegati.map((a) => a.nome).join(', ')}</p>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * «Invita a scrivere»: gli utenti attivi che usano la posta e non scrivono già dalla casella. L'invitato
 * riceve un avviso, vede e modifica solo questa bozza, e non la spedisce.
 */
function InvitaAScrivere({ bozzaId, risposta, chiudi, invitati }: {
  bozzaId: string
  risposta: boolean
  chiudi: () => void
  invitati: (elenco: bozze.Invito[]) => void
}) {
  const [candidati, setCandidati] = useState<(bozze.Persona & { email: string })[] | null>(null)
  const [cerca, setCerca] = useState('')
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState<string | null>(null)

  useEffect(() => {
    bozze.elencaInvitabili(bozzaId).then(setCandidati).catch((e) => setErrore(testoErrore(e)))
  }, [bozzaId])

  async function scegli(utenteId: string) {
    setInCorso(utenteId); setErrore(null)
    try {
      invitati(await bozze.invita(bozzaId, utenteId))
      setCandidati((c) => c?.filter((u) => u.id !== utenteId) ?? null)
    } catch (e) { setErrore(testoErrore(e)) } finally { setInCorso(null) }
  }

  const filtro = cerca.trim().toLowerCase()
  const visibili = (candidati ?? []).filter((u) => !filtro
    || `${u.nome} ${u.cognome} ${u.email}`.toLowerCase().includes(filtro))

  return (
    <Modale titolo="Invita a scrivere" chiudi={chiudi}>
      <Avviso tipo="informazione">
        Chi inviti vede e modifica solo questa bozza, non il resto della casella, e non la spedisce: la
        spedisci tu, o chi scrive dalla casella. Riceve un avviso nel gestionale e sul telefono.
        {risposta && <> Vedrà anche il messaggio a cui rispondi.</>}
      </Avviso>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <Campo etichetta="Cerca un collega" tipo="search" valore={cerca} cambia={setCerca}
             segnaposto="nome, cognome o indirizzo" />
      {!candidati && !errore && <Attesa testo="Caricamento…" />}
      {visibili.map((u) => (
        <button key={u.id} type="button" className="posta-scelta" disabled={inCorso !== null}
                onClick={() => void scegli(u.id)}>
          <span>{u.nome} {u.cognome}</span>
          <span className="tenue">{u.email}</span>
        </button>
      ))}
      {candidati && visibili.length === 0 && (
        <p className="piccolo tenue">Nessun collega da invitare{filtro ? ' con questo nome' : ''}.</p>
      )}
    </Modale>
  )
}
